import React, { useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Card from "@amzn/meridian/card";
import checkLargeTokens from "@amzn/meridian-tokens/base/icon/check-large";
import { useLocation, useNavigate } from "react-router-dom";
import { ItemFlowContext } from "src/types/containerTypes";
import PillButton from "src/components/UI/PillButton";

interface AddedToContainerLocationState {
  itemFlowContext: ItemFlowContext;
  totalQuantity: number;
  totalWeight?: number;
  weightUnit?: string;
  allItemsProcessed?: boolean;
  isMissingReport?: boolean;
}

const AddedToContainerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AddedToContainerLocationState | undefined;

  const itemFlowContext = state?.itemFlowContext;
  const allItemsProcessed = state?.allItemsProcessed ?? false;
  const isMissingReport = state?.isMissingReport ?? false;

  const handleBackToTote = () => {
    if (itemFlowContext?.type !== "tote" && itemFlowContext?.type !== "toteExtraItem") return;
    navigate("/tote/items", {
      replace: true,
      state: {
        scannableId: itemFlowContext.scannableId,
        toteContainerId: itemFlowContext.toteContainerId,
      },
    });
  };

  const handleReportExtraItem = () => {
    if (itemFlowContext?.type !== "tote" && itemFlowContext?.type !== "toteExtraItem") return;
    navigate("/classification/scan", {
      replace: true,
      state: {
        title: "Report Extra Item",
        mode: "PRODUCT",
        itemFlowContext: {
          type: "toteExtraItem",
          toteContainerId: itemFlowContext.toteContainerId,
          scannableId: itemFlowContext.scannableId,
          allItemsProcessed:
            itemFlowContext.type === "tote" ? allItemsProcessed : itemFlowContext.allItemsProcessed,
        } as ItemFlowContext,
      },
    });
  };

  const handleGoHome = () => {
    navigate("/home", { replace: true });
  };

  // Intercept browser back gesture (swipe back on iOS/Android) and redirect
  // to the correct destination instead of going back to the stale confirm page.
  useEffect(() => {
    const handlePopState = () => {
      if (!itemFlowContext) {
        navigate("/home", { replace: true });
        return;
      }
      if (itemFlowContext.type === "tote" || itemFlowContext.type === "toteExtraItem") {
        navigate("/tote/items", {
          replace: true,
          state: {
            scannableId: itemFlowContext.scannableId,
            toteContainerId: itemFlowContext.toteContainerId,
          },
        });
      } else {
        navigate("/home", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, itemFlowContext]);

  if (!state || !itemFlowContext) {
    return (
      <Column
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
        spacingInset="500"
      >
        <Text type="h200">No data found</Text>
        <PillButton text="Go Home" color="secondary" onClick={() => navigate("/home")} />
      </Column>
    );
  }

  const successMessage = (() => {
    if (isMissingReport) return "Successfully marked as missing";
    if (itemFlowContext.type === "tote" && allItemsProcessed)
      return `Tote ${itemFlowContext.scannableId} classification complete`;
    if (itemFlowContext.type === "toteExtraItem")
      return "Extra item successfully added to container";
    
    return "Successfully added to container";
  })();

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="600 500">
      <Row alignmentVertical="center" spacing="300">
        <Icon tokens={checkLargeTokens} color="success" />
        <Text type="h200" color="success">
          {successMessage}
        </Text>
      </Row>

      <Card spacingInset="400">
        <Row spacing="600">
          <Column spacing="100">
            <Text type="b300" color="secondary">
              Total Quantity
            </Text>
            <Text type="h300" color="success">
              {state.totalQuantity}
            </Text>
          </Column>
          {state.totalWeight !== undefined && state.weightUnit && (
            <Column spacing="100">
              <Text type="b300" color="secondary">
                Total Weight
              </Text>
              <Text type="h300" color="success">
                {state.totalWeight.toFixed(3)} {state.weightUnit}
              </Text>
            </Column>
          )}
        </Row>
      </Card>

      {/* Classification flow actions */}
      {itemFlowContext.type === "classification" && (
        <Column spacingInset="400 none" spacing="300">
          <PillButton
            text="SCAN ANOTHER ITEM"
            color="secondary"
            onClick={() => navigate("/classification/scan", { replace: true })}
          />
          <PillButton text="GO HOME" color="outline" onClick={handleGoHome} />
        </Column>
      )}

      {/* Tote flow actions */}
      {itemFlowContext.type === "tote" && (
        <>
          {allItemsProcessed ? (
            <Column spacingInset="400 none" spacing="300">
              <Text type="h300">Items Remaining in Tote?</Text>
              <Text type="b300" color="secondary">
                Before closing, confirm whether any physical items remain that are not in the
                manifest.
              </Text>
              <PillButton
                text="YES, REPORT EXTRA ITEM"
                color="secondary"
                onClick={handleReportExtraItem}
              />
              <PillButton text="NO, CLOSE TOTE" color="outline" onClick={handleGoHome} />
            </Column>
          ) : (
            <PillButton text="BACK TO TOTE" color="secondary" onClick={handleBackToTote} />
          )}
        </>
      )}

      {/* Report extra item flow actions — stays within tote context */}
      {itemFlowContext.type === "toteExtraItem" && (
        <Column spacingInset="400 none" spacing="300">
          <PillButton
            text="REPORT ANOTHER EXTRA ITEM"
            color="secondary"
            onClick={handleReportExtraItem}
          />
          {itemFlowContext.allItemsProcessed ? (
            <PillButton text="CLOSE TOTE" color="outline" onClick={handleGoHome} />
          ) : (
            <PillButton text="BACK TO TOTE" color="outline" onClick={handleBackToTote} />
          )}
        </Column>
      )}
    </Column>
  );
};

export default AddedToContainerPage;
